import { EmptyState, LeagueIdentityHeader, NavButton, PerspectiveSwitch } from "../components/shared";
import { dateLabel, usPhoneDigits, usPhoneForApi } from "../lib/format";
import { memberApi } from "../memberApi";
import { MatchPrepView } from "../screens/match";
import {
  Building2,
  CalendarDays,
  ChevronRight,
  Home,
  Radio,
  ShieldCheck,
  Users,
} from "lucide-react";
import { useEffect, useState } from "react";

export function CaptainWorkspace({
  teams,
  organizationName,
  online,
  onPlayerView,
  onSwitch,
  canSwitchOrganization,
}) {
  const [teamId, setTeamId] = useState(teams[0]?.id || "");
  const [detail, setDetail] = useState(null);
  const [registrationId, setRegistrationId] = useState("");
  const [section, setSection] = useState("home");
  const [roster, setRoster] = useState([]);
  const [joinRequests, setJoinRequests] = useState([]);
  const [available, setAvailable] = useState([]);
  const [delegations, setDelegations] = useState([]);
  const [newPlayer, setNewPlayer] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
  });
  const [emailLookup, setEmailLookup] = useState(null);
  const [forceAcceptPlayer, setForceAcceptPlayer] = useState(false);
  const [rosterEmailEditor, setRosterEmailEditor] = useState(null);
  const [savingRosterEmail, setSavingRosterEmail] = useState(false);
  const [delegateScope, setDelegateScope] = useState("Team");
  const [delegateMatchId, setDelegateMatchId] = useState("");
  const [notice, setNotice] = useState("");
  const [invitation, setInvitation] = useState(null);
  const refresh = async (
    selectedTeam = teamId,
    selectedRegistration = registrationId,
  ) => {
    const nextDetail = await memberApi.captainTeam(selectedTeam);
    setDetail(nextDetail);
    const visibleRegistrations = nextDetail.registrations.filter(
      (value) => value.isSeasonActive,
    );
    const preferred =
      visibleRegistrations.find((value) => value.status !== "Registered") ||
      visibleRegistrations[0] ||
      nextDetail.registrations[0];
    const nextRegistration =
      selectedRegistration &&
      nextDetail.registrations.some(
        (value) => value.id === selectedRegistration,
      )
        ? selectedRegistration
        : preferred?.id || "";
    setRegistrationId(nextRegistration);
    setDelegations(await memberApi.delegations(selectedTeam));
    if (nextRegistration) {
      const [nextRoster, nextAvailable, nextJoinRequests] = await Promise.all([
        memberApi.captainRoster(nextRegistration),
        memberApi.availableMembers(nextRegistration),
        memberApi.captainJoinRequests(nextRegistration),
      ]);
      setRoster(nextRoster);
      setAvailable(nextAvailable);
      setJoinRequests(nextJoinRequests);
    } else {
      setRoster([]);
      setAvailable([]);
      setJoinRequests([]);
    }
  };
  useEffect(() => {
    if (teamId) refresh(teamId, "").catch((error) => setNotice(error.message));
  }, [teamId]);
  if (!detail)
    return (
      <div className="launch-screen">
        <b>OPENING CAPTAIN TOOLS</b>
      </div>
    );
  const registration = detail.registrations.find(
    (value) => value.id === registrationId,
  );
  const activeRegistrations = detail.registrations.filter(
    (value) => value.isSeasonActive,
  );
  const registrationOptions = activeRegistrations.length
    ? activeRegistrations
    : detail.registrations;
  const registrationMatches = detail.matches.filter(
    (match) => match.teamRegistrationId === registrationId,
  );
  const nextMatch = registrationMatches[0];
  const acceptedRoster = roster.filter(
    (player) => player.rosterStatus === "Accepted",
  );
  const missingPlayers = Math.max(0, 6 - acceptedRoster.length);
  const registrationPending =
    registration && registration.status !== "Registered";
  const matchForView = nextMatch
    ? { ...nextMatch, matchId: nextMatch.id }
    : null;
  const captainMemberTeams = registration
    ? [{ registrationId: registration.id, isCaptain: true }]
    : [];
  const submitRegistration = async () => {
    try {
      await memberApi.submitTeamRegistration(registration.id);
      setNotice("Team registration submitted to the league.");
      await refresh();
    } catch (error) {
      setNotice(error.message);
    }
  };
  const setAcceptingPlayers = async (isAcceptingPlayers) => {
    try {
      await memberApi.setCaptainTeamAcceptingPlayers(
        registration.id,
        isAcceptingPlayers,
      );
      setNotice(
        isAcceptingPlayers
          ? "Your team is now visible to players looking for a team."
          : "Player requests are closed for this team.",
      );
      await refresh();
    } catch (error) {
      setNotice(error.message);
    }
  };
  const respondToJoinRequest = async (requestId, approve) => {
    try {
      const result = approve
        ? await memberApi.approveCaptainJoinRequest(registration.id, requestId)
        : await memberApi.rejectCaptainJoinRequest(registration.id, requestId);
      setNotice(result.message);
      await refresh();
    } catch (error) {
      setNotice(error.message);
    }
  };
  return (
    <main className="captain-workspace" data-section={section}>
      <LeagueIdentityHeader
        organizationName={organizationName}
        leagueName={registration?.leagueName}
        online={online}
        offlineLabel="Offline"
        onSwitchOrganization={onSwitch}
        canSwitchOrganization={canSwitchOrganization}
      />
      <PerspectiveSwitch
        perspective="team"
        captainTeams={teams}
        onChange={(value) => value === "player" && onPlayerView()}
      />
      <section className="captain-hero">
        <span className="kicker">TEAM DASHBOARD</span>
        {teams.length > 1 && (
          <select
            aria-label="Choose team"
            value={teamId}
            onChange={(event) => {
              setDetail(null);
              setTeamId(event.target.value);
              setSection("home");
            }}
          >
            {teams.map((team) => (
              <option key={team.id} value={team.id}>
                {team.name}
              </option>
            ))}
          </select>
        )}
        <h1>{detail.name}</h1>
        <p>
          Manage registration, season roster, and match night from one place.
        </p>
      </section>
      {registrationOptions.length > 1 && (
        <label className="captain-select">
          <span>Active season</span>
          <select
            value={registrationId}
            onChange={(event) =>
              refresh(teamId, event.target.value).catch((error) =>
                setNotice(error.message),
              )
            }
          >
            {registrationOptions.map((item) => (
              <option key={item.id} value={item.id}>
                {item.seasonName} {item.year} · {item.nightName}
              </option>
            ))}
          </select>
        </label>
      )}
      {registration ? (
        <>
          {section === "home" && (
            <section className="captain-overview">
              <article
                className={`captain-task-card ${registrationPending ? "needs-action" : "complete"}`}
              >
                <div>
                  <span className="kicker">
                    {registrationPending
                      ? "REGISTRATION IN PROGRESS"
                      : "SEASON READY"}
                  </span>
                  <h2>
                    {registration.seasonName} {registration.year}
                  </h2>
                  <p>
                    {registration.divisionName} · {registration.nightName}
                    {registration.weekday
                      ? ` · ${registration.weekday}`
                      : ""}
                  </p>
                </div>
                <strong>{registration.status}</strong>
                {registrationPending && missingPlayers > 0 ? (
                  <button
                    className="action-primary"
                    onClick={() => setSection("roster")}
                  >
                    <span>
                      Add {missingPlayers} more player
                      {missingPlayers === 1 ? "" : "s"}
                    </span>
                    <ChevronRight />
                  </button>
                ) : registrationPending ? (
                  <button
                    className="action-primary"
                    onClick={submitRegistration}
                  >
                    <span>Submit team registration</span>
                    <ChevronRight />
                  </button>
                ) : (
                  <div className="registration-ready">
                    <ShieldCheck />
                    <span>
                      Registration submitted. You can still add or drop players
                      during the active season.
                    </span>
                  </div>
                )}
              </article>
              <article className="captain-card captain-recruitment-card">
                <header>
                  <div>
                    <span className="kicker">PLAYER RECRUITMENT</span>
                    <h2>
                      {registration.isAcceptingPlayers
                        ? "Accepting requests"
                        : "Requests closed"}
                    </h2>
                  </div>
                  <button
                    className={`recruitment-toggle ${registration.isAcceptingPlayers ? "active" : ""}`}
                    onClick={() =>
                      setAcceptingPlayers(!registration.isAcceptingPlayers)
                    }
                  >
                    {registration.isAcceptingPlayers ? "Close" : "Open"}
                  </button>
                </header>
                <p>
                  {registration.isAcceptingPlayers
                    ? "Players can find this team in public league discovery and ask to join."
                    : "This team is hidden from recruiting results. You can reopen requests at any time."}
                </p>
                {joinRequests.length > 0 ? (
                  <div className="captain-join-requests">
                    <b>
                      {joinRequests.length} pending{" "}
                      {joinRequests.length === 1 ? "request" : "requests"}
                    </b>
                    {joinRequests.map((request) => (
                      <div className="captain-player" key={request.id}>
                        <span>
                          <b>
                            {request.firstName} {request.lastName}
                          </b>
                          <small>Member #{request.membershipIdNumber}</small>
                        </span>
                        <div>
                          <button
                            onClick={() =>
                              respondToJoinRequest(request.id, false)
                            }
                          >
                            Decline
                          </button>
                          <button
                            className="approve"
                            onClick={() =>
                              respondToJoinRequest(request.id, true)
                            }
                          >
                            Approve
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <small className="no-join-requests">
                    No pending player requests.
                  </small>
                )}
              </article>
              <button
                className="captain-match-card"
                onClick={() => setSection("match")}
              >
                <span className="kicker">NEXT MATCH</span>
                <h2>
                  {nextMatch
                    ? `${nextMatch.isHome ? "vs" : "at"} ${nextMatch.opponentName}`
                    : "Schedule coming soon"}
                </h2>
                <p>
                  {nextMatch
                    ? `${dateLabel(nextMatch.matchDate)} · ${nextMatch.pubName || "Venue to be announced"}`
                    : "Your next match will appear when the schedule is published."}
                </p>
                <span>
                  Open match night <ChevronRight />
                </span>
              </button>
              <div className="captain-summary-grid">
                <button onClick={() => setSection("roster")}>
                  <b>{acceptedRoster.length}</b>
                  <span>Accepted players</span>
                </button>
                <button onClick={() => setSection("tools")}>
                  <b>{delegations.length}</b>
                  <span>Captain helpers</span>
                </button>
              </div>
              <div className="account-actions">
                <button onClick={onSwitch}>
                  <Building2 /> Switch organization
                </button>
              </div>
            </section>
          )}
          {section === "roster" && (
            <section className="captain-card captain-roster-card">
              <span className="kicker">
                {registration.seasonName} {registration.year}
              </span>
              <h2>Season roster</h2>
              <p>
                {registration.status === "Registered"
                  ? "Add or drop players as the active season changes."
                  : `${acceptedRoster.length} of 6 required players accepted. Pending players can accept themselves, or you can accept for them on match night.`}
              </p>
              {roster.map((player) => (
                <div className="captain-player-contact" key={player.rosterMemberId}>
                  <div className="captain-player">
                    <span>
                      <b>
                        {player.firstName} {player.lastName}
                      </b>
                      <small>
                        {player.rosterStatus === "Accepted"
                          ? "Accepted roster player"
                          : `${player.rosterStatus} invitation${player.invitationExpiresAt ? ` · expires ${dateLabel(player.invitationExpiresAt.slice(0, 10))}` : ""}`}
                      </small>
                      <small className="roster-contact-email">
                        {player.email || "No email on file"}
                      </small>
                    </span>
                    <div>
                      {player.claimStatus !== "Claimed" && (
                        <button
                          onClick={() =>
                            setRosterEmailEditor({
                              playerMembershipId: player.playerMembershipId,
                              email: player.email || "",
                            })
                          }
                        >
                          {player.email ? "Email / invite" : "Add email & invite"}
                        </button>
                      )}
                      {player.rosterStatus === "Pending" && (
                        <button
                          className="approve"
                          onClick={async () => {
                            await memberApi.forceAcceptRosterMember(
                              registration.id,
                              player.playerMembershipId,
                            );
                            setNotice(
                              `${player.firstName} ${player.lastName} is accepted and can play tonight.`,
                            );
                            await refresh();
                          }}
                        >
                          Accept now
                        </button>
                      )}
                      <button
                        title="Remove player"
                        onClick={async () => {
                          await memberApi.removeRosterMember(
                            registration.id,
                            player.playerMembershipId,
                          );
                          await refresh();
                        }}
                      >
                        ×
                      </button>
                    </div>
                  </div>
                  {rosterEmailEditor?.playerMembershipId === player.playerMembershipId && (
                    <form
                      className="captain-roster-email-form"
                      onSubmit={async (event) => {
                        event.preventDefault();
                        const email = rosterEmailEditor.email.trim().toLowerCase();
                        setSavingRosterEmail(true);
                        try {
                          await memberApi.updateRosterMemberEmail(
                            registration.id,
                            player.playerMembershipId,
                            email,
                          );
                          const claim = await memberApi.inviteRosterPlayer(
                            player.playerMembershipId,
                            "Email",
                            email,
                          );
                          await memberApi.sendRosterPlayerInvitation(
                            player.playerMembershipId,
                            claim,
                          );
                          setInvitation({
                            invitationUrl: claim.claimUrl,
                            deliveryChannel: "Email",
                            deliveryTarget: email,
                            requiresChannelChoice: false,
                          });
                          setRosterEmailEditor(null);
                          setNotice(
                            `${player.firstName} ${player.lastName}'s email was saved and their account invitation was sent.`,
                          );
                          await refresh();
                        } catch (error) {
                          setNotice(error.message);
                        } finally {
                          setSavingRosterEmail(false);
                        }
                      }}
                    >
                      <label>
                        <span>Email address</span>
                        <input
                          required
                          type="email"
                          autoComplete="email"
                          value={rosterEmailEditor.email}
                          onChange={(event) =>
                            setRosterEmailEditor({
                              ...rosterEmailEditor,
                              email: event.target.value,
                            })
                          }
                          placeholder="player@example.com"
                        />
                      </label>
                      <button type="button" onClick={() => setRosterEmailEditor(null)}>
                        Cancel
                      </button>
                      <button className="approve" disabled={savingRosterEmail}>
                        {savingRosterEmail ? "Sending…" : "Save & send invite"}
                      </button>
                    </form>
                  )}
                </div>
              ))}
              {invitation && (
                <div className="captain-notice">
                  <b>
                    {invitation.deliveryChannel === "Email"
                      ? `Invitation email queued for ${invitation.deliveryTarget}.`
                      : invitation.requiresChannelChoice
                        ? "Choose how to send this invitation."
                        : "Invitation link created."}
                  </b>
                  <button
                    onClick={async () => {
                      await navigator.clipboard.writeText(
                        invitation.invitationUrl,
                      );
                      setNotice("Invitation link copied.");
                    }}
                  >
                    Copy invitation link
                  </button>
                  {invitation.requiresChannelChoice && (
                    <small>
                      SMS may be chosen only because this player already
                      consented. Copy the link now, or add the player again and
                      choose their preferred method.
                    </small>
                  )}
                </div>
              )}
              {available.length > 0 && (
                <div className="add-existing">
                  <select defaultValue="">
                    <option value="" disabled>
                      Invite an organization member
                    </option>
                    {available.map((player) => (
                      <option
                        key={player.playerMembershipId}
                        value={player.playerMembershipId}
                      >
                        {player.firstName} {player.lastName}
                      </option>
                    ))}
                  </select>
                  <button
                    onClick={async (event) => {
                      const select = event.currentTarget.previousElementSibling;
                      if (!select.value) return;
                      let result = await memberApi.addRosterMember(
                        registration.id,
                        select.value,
                      );
                      if (result.requiresChannelChoice) {
                        const channel = confirm(
                          "This player consented to SMS but has not selected a preference. Press OK to send SMS, or Cancel to send email.",
                        )
                          ? "Sms"
                          : "Email";
                        result = await memberApi.addRosterMember(
                          registration.id,
                          select.value,
                          channel,
                        );
                      }
                      setInvitation(result);
                      setNotice(
                        "Player invited. Their roster place remains pending until accepted.",
                      );
                      await refresh();
                    }}
                  >
                    Invite
                  </button>
                </div>
              )}
              <details>
                <summary>Add a new player for tonight</summary>
                <form
                  className="captain-new-player"
                  autoComplete="on"
                  onSubmit={async (event) => {
                    event.preventDefault();
                    const form = event.currentTarget;
                    const submitted = new FormData(form);
                    const player = {
                      firstName: String(
                        submitted.get("firstName") || "",
                      ).trim(),
                      lastName: String(submitted.get("lastName") || "").trim(),
                      email: String(submitted.get("email") || "").trim(),
                      phone: usPhoneForApi(submitted.get("phone")),
                      forceAccept: submitted.get("forceAccept") === "on",
                    };
                    setNewPlayer({
                      firstName: player.firstName,
                      lastName: player.lastName,
                      email: player.email,
                      phone: usPhoneDigits(player.phone),
                    });
                    try {
                      const result = emailLookup?.found
                        ? await memberApi.addRosterMember(
                            registration.id,
                            emailLookup.playerMembershipId,
                            null,
                            player.forceAccept,
                          )
                        : await memberApi.addNewRosterPlayer(
                            registration.id,
                            player,
                          );
                      setInvitation(result.invitationUrl ? result : null);
                      setNewPlayer({
                        firstName: "",
                        lastName: "",
                        email: "",
                        phone: "",
                      });
                      setEmailLookup(null);
                      setForceAcceptPlayer(false);
                      form.reset();
                      await refresh();
                      setNotice(
                        `${player.firstName} ${player.lastName} was added${player.forceAccept || (!player.email && !player.phone) ? " and accepted for tonight" : ". Their acceptance is pending"}.`,
                      );
                    } catch (error) {
                      setNotice(error.message);
                    }
                  }}
                >
                  <label>
                    <span>First name</span>
                    <input
                      name="firstName"
                      required
                      autoComplete="given-name"
                      placeholder="First name"
                      value={newPlayer.firstName}
                      onChange={(event) =>
                        setNewPlayer({
                          ...newPlayer,
                          firstName: event.target.value,
                        })
                      }
                    />
                  </label>
                  <label>
                    <span>Last name</span>
                    <input
                      name="lastName"
                      required
                      autoComplete="family-name"
                      placeholder="Last name"
                      value={newPlayer.lastName}
                      onChange={(event) =>
                        setNewPlayer({
                          ...newPlayer,
                          lastName: event.target.value,
                        })
                      }
                    />
                  </label>
                  <label>
                    <span>
                      Email{" "}
                      <small>required only when the name already exists</small>
                    </span>
                    <input
                      name="email"
                      type="email"
                      autoComplete="email"
                      placeholder="Email (optional)"
                      value={newPlayer.email}
                      onChange={(event) => {
                        setNewPlayer({
                          ...newPlayer,
                          email: event.target.value,
                        });
                        setEmailLookup(null);
                      }}
                      onBlur={async (event) => {
                        const email = event.target.value.trim();
                        if (!email) return;
                        try {
                          setEmailLookup(
                            await memberApi.memberByEmail(
                              registration.id,
                              email,
                            ),
                          );
                        } catch (error) {
                          setNotice(error.message);
                        }
                      }}
                    />
                  </label>
                  {emailLookup?.found && (
                    <div
                      className={`captain-notice ${emailLookup.isAvailable ? "" : "error"}`}
                    >
                      <b>
                        {emailLookup.firstName} {emailLookup.lastName} ·{" "}
                        {emailLookup.membershipDisplayId}
                      </b>
                      <small>
                        {emailLookup.isAvailable
                          ? "Existing organization player found. Submitting will use this record."
                          : "This player is already assigned to a team in this season."}
                      </small>
                    </div>
                  )}
                  <label>
                    <span>
                      Phone <small>optional</small>
                    </span>
                    <div className="us-phone-input">
                      <span>+1</span>
                      <input
                        name="phone"
                        autoComplete="tel-national"
                        inputMode="numeric"
                        maxLength="10"
                        placeholder="Phone (optional)"
                        value={usPhoneDigits(newPlayer.phone)}
                        onChange={(event) =>
                          setNewPlayer({
                            ...newPlayer,
                            phone: event.target.value
                              .replace(/\D/g, "")
                              .slice(0, 10),
                          })
                        }
                      />
                    </div>
                  </label>
                  <label className="claim-consent">
                    <input
                      name="forceAccept"
                      type="checkbox"
                      checked={forceAcceptPlayer}
                      onChange={(event) => setForceAcceptPlayer(event.target.checked)}
                    />
                    <span>
                      <b>Accept for this player now</b>
                      <small>
                        Use when the player cannot accept on a device. No
                        contact details automatically uses this match-night
                        exception.
                      </small>
                    </span>
                  </label>
                  <button
                    className="action-primary"
                    disabled={emailLookup?.found && !emailLookup.isAvailable}
                  >
                    {emailLookup?.found
                      ? "Use this player record"
                      : "Add player"}
                  </button>
                </form>
              </details>
              {registrationPending && acceptedRoster.length >= 6 && (
                <button
                  className="action-primary roster-submit"
                  onClick={submitRegistration}
                >
                  <span>Submit completed registration</span>
                  <ChevronRight />
                </button>
              )}
            </section>
          )}
          {section === "match" && (
            <MatchPrepView
              match={matchForView}
              teams={captainMemberTeams}
              online={online}
              showCurrentMatchStatus
              allowLineupManagement
              teamContext
            />
          )}
          {section === "tools" && (
            <section className="captain-card captain-helpers-card">
              <span className="kicker">DELEGATION</span>
              <h2>Captain helpers</h2>
              <p>
                Delegates can create lineups and approve completed matches. They
                cannot change the roster, invite players, submit registration,
                or appoint another delegate.
              </p>
              {delegations.map((item) => (
                <div className="captain-player" key={item.id}>
                  <span>
                    <b>{item.playerName}</b>
                    <small>
                      {item.scope === "Team"
                        ? "Permanent until revoked"
                        : "This match only"}
                    </small>
                  </span>
                  <button
                    onClick={async () => {
                      await memberApi.revokeDelegation(teamId, item.id);
                      await refresh();
                    }}
                  >
                    Revoke
                  </button>
                </div>
              ))}
              <div className="delegate-options">
                <select
                  value={delegateScope}
                  onChange={(event) => setDelegateScope(event.target.value)}
                >
                  <option value="Team">Permanent delegation</option>
                  <option value="Match">One match only</option>
                </select>
                {delegateScope === "Match" && (
                  <select
                    required
                    value={delegateMatchId}
                    onChange={(event) => setDelegateMatchId(event.target.value)}
                  >
                    <option value="">Choose a match</option>
                    {registrationMatches.map((match) => (
                      <option key={match.id} value={match.id}>
                        {match.matchDate} · Round {match.roundNumber} vs{" "}
                        {match.opponentName}
                      </option>
                    ))}
                  </select>
                )}
              </div>
              <div className="add-existing">
                <select defaultValue="">
                  <option value="" disabled>
                    Choose an accepted roster player
                  </option>
                  {acceptedRoster.map((player) => (
                    <option
                      key={player.playerMembershipId}
                      value={player.playerMembershipId}
                    >
                      {player.firstName} {player.lastName}
                    </option>
                  ))}
                </select>
                <button
                  onClick={async (event) => {
                    const select = event.currentTarget.previousElementSibling;
                    if (
                      !select.value ||
                      (delegateScope === "Match" && !delegateMatchId)
                    ) {
                      setNotice("Choose both a player and match.");
                      return;
                    }
                    await memberApi.grantDelegation(teamId, {
                      playerMembershipId: select.value,
                      scope: delegateScope,
                      scheduledMatchId:
                        delegateScope === "Match" ? delegateMatchId : null,
                      expiresAt: null,
                    });
                    await refresh();
                  }}
                >
                  Delegate
                </button>
              </div>
            </section>
          )}
        </>
      ) : (
        <EmptyState
          icon={<CalendarDays />}
          title="No season registration"
          text="Use an open season registration link to enter this team."
        />
      )}
      {notice && <p className="captain-notice">{notice}</p>}
      <nav
        className="bottom-nav captain-dashboard-nav"
        aria-label="Team dashboard navigation"
      >
        <NavButton
          active={section === "home"}
          onClick={() => setSection("home")}
          icon={<Home />}
          label="Team"
        />
        <NavButton
          active={section === "roster"}
          onClick={() => setSection("roster")}
          icon={<Users />}
          label="Roster"
        />
        <NavButton
          prominent
          active={section === "match"}
          onClick={() => setSection("match")}
          icon={<Radio />}
          label="Match"
        />
        <NavButton
          active={section === "tools"}
          onClick={() => setSection("tools")}
          icon={<ShieldCheck />}
          label="Helpers"
        />
      </nav>
    </main>
  );
}
